import type { RequestQuery } from '@hapi/hapi';
import type {
	FilterConfig,
	FilterConfigs,
	FilterValue,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { getVanityFacetValues } from '@pages/VendorsSearch/containers/FilterPills/components/StartingPriceFilter/utils';

type QueryParamEntries = Array<[string, string]>;

function getFilterValue(value: string, filterConfig: FilterConfig) {
	const parseFilterValue = (v: string) => {
		const option = filterConfig.filterOptions.find(
			(o) => o.singular.slug === v,
		);
		return option ? { facetId: option.id } : null;
	};
	switch (filterConfig.type) {
		case 'facet-multi-select': {
			const filterValue = value
				.split(' ')
				.map(parseFilterValue)
				.filter((f): f is { facetId: string } => f !== null);
			return filterValue.length > 0 ? filterValue : null;
		}
		case 'facet-single-select':
			return parseFilterValue(value);
	}
}

function parseOutdoorFacet(filterConfigs: FilterConfigs) {
	const outdoorSpaceConfig = filterConfigs.outdoor_space;
	if (!outdoorSpaceConfig) return [];
	const outdoorSpaceValues = outdoorSpaceConfig.filterOptions
		.map((o) => o.singular.slug)
		.join(' ');
	const filterValue = getFilterValue(outdoorSpaceValues, outdoorSpaceConfig);
	if (!filterValue) return [];
	return [{ id: outdoorSpaceConfig.id, value: filterValue }];
}

function isPricingFacet(facet: string, categoryCode: string): boolean {
	const pricingFacetKeys = Object.keys(getVanityFacetValues);
	// Check if facet matches any pricing facet key
	// e.g., "affordable" should match "REC-affordable" if categoryCode is "REC"
	return pricingFacetKeys.some((key) => {
		const [code, slug] = key.split('-');
		return slug === facet && code === categoryCode;
	});
}

function parsePricingFacet(facet: string, categoryCode: string): FilterValue[] {
	const vanityKey = `${categoryCode}-${facet}`;
	const vanityValues = getVanityFacetValues[vanityKey];

	if (!vanityValues) return [];

	const { minInput, maxInput } = vanityValues;
	const value: { minPrice?: number; maxPrice?: number; vanitySlug?: string } = {
		vanitySlug: facet, // Store the original facet slug
	};

	if (minInput) {
		value.minPrice = Number(minInput);
	}
	if (maxInput) {
		value.maxPrice = Number(maxInput);
	}

	return [{ id: 'starting-price', value }];
}

function parseFilterFromFacet(
	filterConfigs: FilterConfigs,
	facet: string,
	categoryCode: string,
) {
	if (isPricingFacet(facet, categoryCode)) {
		return parsePricingFacet(facet, categoryCode);
	}

	const facetFilterConfig = Object.values(filterConfigs).find((filterConfig) =>
		filterConfig.filterOptions.find(
			(o) => o.singular.slug === facet || o.plural.slug === facet,
		),
	);
	if (!facetFilterConfig) return [];
	const facetFilterValue = getFilterValue(facet, facetFilterConfig);
	if (!facetFilterValue) return [];
	return [{ id: facetFilterConfig.id, value: facetFilterValue }];
}

export function queryParamsToFilterValues(
	queryParams: RequestQuery | QueryParamEntries,
	facet: string | null,
	filterConfigs: FilterConfigs,
	categoryCode = '',
): FilterValue[] {
	if (facet) {
		return facet === 'outdoor'
			? parseOutdoorFacet(filterConfigs)
			: parseFilterFromFacet(filterConfigs, facet, categoryCode);
	}

	const entries = Array.isArray(queryParams)
		? queryParams
		: Object.entries(queryParams);

	return entries.reduce((acc: FilterValue[], [slug, value]) => {
		if (
			slug === 'price_range' &&
			filterConfigs['starting-price']?.type === 'pricing'
		) {
			const pricingFilterValue = parsePricingFacet(value, categoryCode);
			if (pricingFilterValue.length > 0) {
				acc.push(pricingFilterValue[0]);
			}
		} else if (slug === 'minPrice' || slug === 'maxPrice') {
			// Always use 'starting-price' as the ID for pricing filter values
			const pricingIndex = acc.findIndex((f) => f.id === 'starting-price');
			if (pricingIndex !== -1) {
				const pricing = acc[pricingIndex];
				acc[pricingIndex] = {
					...pricing,
					value: {
						...pricing.value,
						[slug]: Number(value),
					},
				};
			} else {
				acc.push({ id: 'starting-price', value: { [slug]: Number(value) } });
			}
		} else {
			const filterConfig = filterConfigs[slug];
			if (!filterConfig) return acc;
			const filterValue = getFilterValue(value, filterConfig);
			if (filterValue) {
				acc.push({ id: filterConfig.id, value: filterValue });
			}
		}
		return acc;
	}, []);
}
